// Workspace App — Admin "View as" tier context.
// Session-only tier override for admins. Provides:
//   - `viewAsTier`  — the currently-overridden tier (null if none)
//   - `setViewAs(tier)` — set/clear the override (persists via backend + localStorage)
//   - `effectiveTier(realTier)` — helper returning viewAsTier || realTier
//
// Persistent banner at the top of every tier-gated page while active.
// Tier-gated components import `useViewAs()` and consult `effectiveTier()`
// instead of the user's real tier so the preview is consistent everywhere.

import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const LS_KEY = "workspace_view_as_tier";
const Ctx = React.createContext({
  viewAsTier: null,
  setViewAs: () => {},
  effectiveTier: (real) => real,
  isOverriding: false,
});

export function ViewAsProvider({ children }) {
  const [viewAsTier, setViewAsTier] = React.useState(() => {
    try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
  });

  // Sync with backend on mount (backend cookie is authoritative for
  // reload / cross-tab consistency).
  React.useEffect(() => {
    api.get("/workspace/admin/view-as", { skipAuthRedirect: true }).then(({ data }) => {
      if (data?.tier !== undefined) {
        setViewAsTier(data.tier || null);
        try {
          if (data.tier) localStorage.setItem(LS_KEY, data.tier);
          else localStorage.removeItem(LS_KEY);
        } catch { /* private mode */ }
      }
    }).catch(() => { /* non-admin — silently ignore */ });
  }, []);

  const setViewAs = React.useCallback(async (tier) => {
    try {
      const { data } = await api.post("/workspace/admin/view-as", { tier });
      setViewAsTier(data.tier || null);
      try {
        if (data.tier) localStorage.setItem(LS_KEY, data.tier);
        else localStorage.removeItem(LS_KEY);
      } catch { /* ignore */ }
      toast.success(data.tier ? `Now viewing as ${data.tier}` : "View-as cleared");
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to update view-as";
      toast.error(typeof msg === "string" ? msg : "Failed to update view-as");
    }
  }, []);

  const effectiveTier = React.useCallback((realTier) => viewAsTier || realTier, [viewAsTier]);

  const value = React.useMemo(() => ({
    viewAsTier, setViewAs, effectiveTier, isOverriding: !!viewAsTier,
  }), [viewAsTier, setViewAs, effectiveTier]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useViewAs() {
  return React.useContext(Ctx);
}

// Persistent banner rendered globally when a view-as override is active.
// Kept unmissable per user spec: bright color, sticky at top, one-click
// dismiss ("Return to Admin view").
export function ViewAsBanner() {
  const { viewAsTier, setViewAs } = useViewAs();
  if (!viewAsTier) return null;
  return (
    <div
      className="sticky top-0 z-40 flex items-center justify-center gap-2 py-1.5 text-[11px] font-bold"
      style={{ background: "#f59e0b", color: "#0a0a0a" }}
      data-testid="view-as-banner"
    >
      <span>Viewing as: {viewAsTier.toUpperCase()} user</span>
      <button
        onClick={() => setViewAs(null)}
        className="underline hover:no-underline flex items-center gap-1"
        data-testid="view-as-banner-clear"
      >
        <X className="w-3 h-3" />
        Return to Admin view
      </button>
    </div>
  );
}

// Small dropdown for admins to enter the view-as mode.
export function ViewAsSwitcher() {
  const { viewAsTier, setViewAs } = useViewAs();
  return (
    <select
      className="h-6 text-[10px] border rounded-sm px-1"
      style={{
        background: "var(--hoip-card)",
        borderColor: "var(--hoip-border)",
        color: "var(--hoip-text-primary)",
      }}
      value={viewAsTier || ""}
      onChange={e => setViewAs(e.target.value || null)}
      data-testid="view-as-switcher"
    >
      <option value="">View as: Admin (real)</option>
      <option value="demo">View as: Demo</option>
      <option value="free">View as: Free</option>
      <option value="trial">View as: Trial</option>
      <option value="pro">View as: Pro</option>
      <option value="beta">View as: Beta</option>
    </select>
  );
}
